/**
 * O estado da página do Desk Cloud (docs/spec/desk-cloud.md): o lugar aberto, o filtro, a busca, a
 * ordem, a página de arquivos, o resumo da lateral, a seleção, o arquivo dos detalhes e a fila de
 * envios. As ações que falam com o servidor ficam em cloudActions.ts.
 */
import { create } from 'zustand';
import { api } from '../api.js';
import { describeError, useApp } from '../store.js';
import type { DeskCloudSummary, DeskFile, FileKind, FileSort } from '../shared.js';
import { HOME, folderPath, locationExists, nextSelection, sameLocation, type CloudLocation } from './cloudModel.js';

export type Layout = 'grid' | 'list';

/** Um envio na fila: espera, vai (com o progresso de 0 a 1), foi ou falhou. */
export type CloudUpload = {
  id: string;
  file: File;
  /** onde entra: a mesa (liga) e a pasta */
  roomId: string | null;
  folderId: string | null;
  status: 'waiting' | 'sending' | 'done' | 'error';
  progress: number;
  error: string | null;
  /** o plano recusou (o maior arquivo ou o espaço): a fila oferece "Ver planos" */
  planLimited: 'too-large' | 'no-space' | null;
  /** o arquivo, depois de enviado */
  stored: DeskFile | null;
};

type Page = { files: DeskFile[]; total: number; kinds: Record<FileKind, number> };

type CloudState = {
  at: CloudLocation;
  kind: FileKind | null;
  query: string;
  sort: FileSort;
  layout: Layout;
  summary: DeskCloudSummary | null;
  page: Page | null;
  loading: boolean;
  loadingMore: boolean;
  error: string | null;
  selected: Set<string>;
  anchor: string | null;
  /** o arquivo do painel de detalhes */
  focus: string | null;
  uploads: CloudUpload[];
  /** as pastas usadas por último (o "Mover para ▸") */
  recentFolders: string[];
  /** as pastas abertas na árvore da lateral (entrar numa pasta abre o caminho até ela) */
  expanded: Set<string>;

  go: (at: CloudLocation) => void;
  setKind: (kind: FileKind | null) => void;
  setQuery: (query: string) => void;
  setSort: (sort: FileSort) => void;
  setLayout: (layout: Layout) => void;
  /** relê o resumo e a página do lugar aberto */
  load: () => Promise<void>;
  loadMore: () => Promise<void>;
  select: (id: string, mode: 'single' | 'toggle' | 'range') => void;
  setSelection: (ids: Iterable<string>) => void;
  clearSelection: () => void;
  setFocus: (id: string | null) => void;
  usedFolder: (folderId: string) => void;
  toggleFolder: (folderId: string) => void;
  /** os arquivos da página entre `ids` (os que estão na tela) */
  filesOf: (ids: Iterable<string>) => DeskFile[];
  patchUpload: (id: string, patch: Partial<CloudUpload>) => void;
  addUploads: (uploads: CloudUpload[]) => void;
  clearUploads: () => void;
  /** volta ao começo, sem nada da conta (saiu, ou entrou outra nesta aba) */
  reset: () => void;
};

const LAYOUT_KEY = 'rpgdesk.cloud.layout';
const SORT_KEY = 'rpgdesk.cloud.sort';

function stored<T extends string>(key: string, allowed: readonly T[], fallback: T): T {
  try {
    const value = window.localStorage.getItem(key);
    return allowed.includes(value as T) ? (value as T) : fallback;
  } catch {
    return fallback;
  }
}

function remember(key: string, value: string): void {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    // armazenamento indisponível (modo privado): vale só nesta visita
  }
}

/** Cada leitura ganha um número: a resposta de uma leitura que já ficou para trás não vale. */
let generation = 0;

/** O que é da conta: o lugar, a página, a seleção, a fila e as pastas (a ordem e o leiaute ficam). */
const accountState = () => ({
  at: HOME,
  kind: null,
  query: '',
  summary: null,
  page: null,
  loading: false,
  loadingMore: false,
  error: null,
  selected: new Set<string>(),
  anchor: null,
  focus: null,
  uploads: [],
  recentFolders: [],
  expanded: new Set<string>(),
});

export const useCloud = create<CloudState>()((set, get) => ({
  ...accountState(),
  sort: stored(SORT_KEY, ['recent', 'name', 'size'] as const, 'recent'),
  layout: stored(LAYOUT_KEY, ['grid', 'list'] as const, 'grid'),

  go(at) {
    if (sameLocation(at, get().at)) return;
    set({ at, kind: null, query: '', selected: new Set(), anchor: null, focus: null, page: null });
    if (at.view === 'folder') {
      get().usedFolder(at.folderId);
      const path = folderPath(get().summary?.folders ?? [], at.folderId).map((folder) => folder.id);
      set((state) => ({ expanded: new Set([...state.expanded, ...path]) }));
    }
    void get().load();
  },
  setKind(kind) {
    set({ kind, selected: new Set(), anchor: null });
    void get().load();
  },
  setQuery(query) {
    set({ query, selected: new Set(), anchor: null });
    void get().load();
  },
  setSort(sort) {
    remember(SORT_KEY, sort);
    set({ sort });
    void get().load();
  },
  setLayout(layout) {
    remember(LAYOUT_KEY, layout);
    set({ layout });
  },

  async load() {
    const token = useApp.getState().token;
    if (!token) return;
    const mine = ++generation;
    const { at, kind, query, sort } = get();
    set({ loading: true });
    try {
      const [summary, page] = await Promise.all([
        api.fileSummary(token),
        api.listFiles(token, {
          view: at.view,
          ...(at.view === 'room' ? { roomId: at.roomId } : {}),
          ...(at.view === 'folder' ? { folderId: at.folderId } : {}),
          ...(kind ? { kind } : {}),
          ...(query.trim() ? { q: query.trim() } : {}),
          sort,
        }),
      ]);
      if (mine !== generation) return;
      // o espaço também vale para o aviso antes de enviar (docs/spec/planos.md)
      useApp.setState({ fileUsage: { used: summary.usage.used, quota: summary.usage.quota } });
      const ids = new Set(page.files.map((file) => file.id));
      set((state) => ({
        summary,
        page,
        loading: false,
        error: null,
        // a seleção e os detalhes ficam só com o que ainda está na tela
        selected: new Set([...state.selected].filter((id) => ids.has(id))),
        focus: state.focus && ids.has(state.focus) ? state.focus : null,
        recentFolders: state.recentFolders.filter((id) => summary.folders.some((folder) => folder.id === id)),
      }));
      // a pasta ou a mesa aberta sumiu (apagada noutra aba): volta ao começo
      if (!locationExists(at, summary)) get().go(HOME);
    } catch (error) {
      if (mine === generation) set({ loading: false, error: describeError(error) });
    }
  },

  async loadMore() {
    const token = useApp.getState().token;
    const { page, at, kind, query, sort, loadingMore } = get();
    if (!token || !page || loadingMore || page.files.length >= page.total) return;
    const mine = generation;
    set({ loadingMore: true });
    try {
      const next = await api.listFiles(token, {
        view: at.view,
        ...(at.view === 'room' ? { roomId: at.roomId } : {}),
        ...(at.view === 'folder' ? { folderId: at.folderId } : {}),
        ...(kind ? { kind } : {}),
        ...(query.trim() ? { q: query.trim() } : {}),
        sort,
        offset: page.files.length,
      });
      if (mine !== generation) return;
      set((state) => {
        if (!state.page) return { loadingMore: false };
        const have = new Set(state.page.files.map((file) => file.id));
        return { loadingMore: false, page: { ...next, files: [...state.page.files, ...next.files.filter((file) => !have.has(file.id))] } };
      });
    } catch (error) {
      if (mine === generation) set({ loadingMore: false, error: describeError(error) });
    }
  },

  select(id, mode) {
    const { selected, anchor, page } = get();
    const order = page?.files.map((file) => file.id) ?? [];
    set(nextSelection(selected, id, mode, order, anchor));
  },
  setSelection(ids) {
    set({ selected: new Set(ids) });
  },
  clearSelection() {
    set({ selected: new Set(), anchor: null });
  },
  setFocus(id) {
    set({ focus: id });
  },
  toggleFolder(folderId) {
    set((state) => {
      const expanded = new Set(state.expanded);
      if (expanded.has(folderId)) expanded.delete(folderId);
      else expanded.add(folderId);
      return { expanded };
    });
  },
  usedFolder(folderId) {
    set((state) => ({ recentFolders: [folderId, ...state.recentFolders.filter((id) => id !== folderId)].slice(0, 8) }));
  },
  filesOf(ids) {
    const wanted = new Set(ids);
    return get().page?.files.filter((file) => wanted.has(file.id)) ?? [];
  },
  patchUpload(id, patch) {
    set((state) => ({ uploads: state.uploads.map((upload) => (upload.id === id ? { ...upload, ...patch } : upload)) }));
  },
  addUploads(uploads) {
    set((state) => ({ uploads: [...state.uploads, ...uploads] }));
  },
  clearUploads() {
    set((state) => ({ uploads: state.uploads.filter((upload) => upload.status === 'waiting' || upload.status === 'sending') }));
  },
  reset() {
    // a leitura em andamento era da conta anterior
    generation += 1;
    set(accountState());
  },
}));
